import DetailsPageTitle from "components/common/DetailsPageTitle";
import Spinners from "components/common/Spinners";
import React, { useEffect, useState } from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import SendNotification from "./SendNotification";
import {
  getAllNotifications,
  updateNotification,
} from "store/actions/notificationSlice";
import NoDataFound from "components/common/NoDataFound";
import { Pencil, X } from "lucide-react";
import { Form, Formik } from "formik";
import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import { updateNotificationSchema } from "components/common/Validation";
import { showAlert } from "store/reducers/alertReducer";
import { InputSwitch } from "primereact/inputswitch";
import Paginations from "components/common/Paginations";

const updateNotificationInitialValues = {
  title: "",
  titleAr: "",
  description: "",
  descriptionAr: "",
  status: null,
};

const Notifications = () => {
  const { t } = useTranslation();
  document.title = t("Notifications | Work Space");

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;

  const {
    isLoading,
    loading,
    notifications,
    perPage,
    totalCount,
    totalPages,
    currentPage,
  } = useSelector((state) => state.Notifications);

  const [show, setShow] = useState(false);
  const [selectedItem, setSelectedItem] = useState({});
  const [formValues, setFormValues] = useState(updateNotificationInitialValues);

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const payload = {
      perPage,
      pageNo: page || pageNumber,
      status: -1,
      orderBy: -1,
      type: [],
      actionType: [],
      dateRange: {},
      keyword: "",
    };

    dispatch(getAllNotifications(payload));

    // navigate(`/management/settings/notifications?page=${page || pageNumber}`);
  };

  const handleClose = () => {
    setShow(false);
    setFormValues(updateNotificationInitialValues);
  };

  const handleEditClick = (item) => {
    const { title, body, status } = item || {};
    setFormValues({
      title: title?.en || "",
      titleAr: title?.ar || "",
      description: body?.en || "",
      descriptionAr: body?.ar || "",
      status: status || null,
    });
    setSelectedItem(item);
    setShow(true);
  };

  const handleUpdateNotification = (values) => {
    const { title, titleAr, description, descriptionAr, status } = values || {};
    const payload = {
      id: selectedItem?._id,
      title: {
        en: title,
        ar: titleAr,
      },
      body: {
        en: description,
        ar: descriptionAr,
      },
      status: String(status),
      remark: "",
    };

    dispatch(updateNotification(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          handleClose();
          getAllData();
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <React.Fragment>
      <div className="page-content common_page notification_page">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <DetailsPageTitle title={t("Notification")} />
              <SendNotification />
              <DetailsPageTitle
                title={t("Notifications Message")}
                isBackBtn={false}
              />
              <div className="common_wrapper">
                <div className="notifications_list">
                  {notifications.length === 0 ? (
                    <NoDataFound />
                  ) : (
                    <>
                      {notifications?.map((item) => (
                        <div className="single_notification" key={item?._id}>
                          <div className="g_between mb-3">
                            <h2 className="fs_20 text-decoration-underline text_primary">
                              {item?.type}
                            </h2>
                            <Pencil
                              className="text_primary c_p"
                              onClick={() => {
                                handleEditClick(item);
                              }}
                            />
                          </div>
                          <div className="g_between">
                            <h2 className="fs_16 w-50">{item?.title?.en}</h2>
                            <h2 className="fs_16 title_ar w-50">
                              {item?.title?.ar}
                            </h2>
                          </div>
                          <div className="g_between desc_row gap-3">
                            <h3 className="fs_14 w-50">{item?.body?.en}</h3>
                            <h3 className="fs_14 title_ar w-50">
                              {item?.body?.ar}
                            </h3>
                          </div>
                        </div>
                      ))}
                      {!loading && (
                        <Paginations
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={(page) => {
                            getAllData(page);
                          }}
                          totalCount={totalCount}
                        />
                      )}
                    </>
                  )}
                </div>
              </div>
            </Row>
          </Container>
        )}
      </div>

      <Modal
        centered
        show={show}
        size="lg"
        onHide={handleClose}
        className="common_modal"
      >
        <div className="modal_header g_between">
          <h2 className="fs_24">{t("Update Notification")}</h2>
          <X
            onClick={handleClose}
            className="text_black c_p"
            width={20}
            height={20}
          />
        </div>
        <div className="modal_body mt-0">
          <Formik
            initialValues={formValues}
            validationSchema={updateNotificationSchema}
            enableReinitialize={true}
            onSubmit={handleUpdateNotification}
          >
            {({ errors, touched, values, setFieldValue }) => (
              <Form>
                <Row>
                  <Col lg={6}>
                    <div className="form_field mt_13 mb-0">
                      <CustomLabel htmlFor="title" label={t("English Title")} />
                      <CustomField
                        name="title"
                        type="text"
                        placeholder={t("English Title")}
                        touched={touched}
                        errors={errors}
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form_field mt_13 mb-0">
                      <CustomLabel
                        htmlFor="titleAr"
                        label={t("Arabic Title")}
                      />
                      <CustomField
                        name="titleAr"
                        type="text"
                        placeholder={t("Arabic Title")}
                        touched={touched}
                        errors={errors}
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form_field mt_13 mb-0">
                      <CustomLabel
                        htmlFor="description"
                        label={t("English Description")}
                      />
                      <CustomField
                        name={`description`}
                        placeholder="English Description"
                        as="textarea"
                        touched={touched}
                        errors={errors}
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form_field mt_13 mb-0">
                      <CustomLabel
                        htmlFor="descriptionAr"
                        label={t("Arabic Description")}
                      />
                      <CustomField
                        name={`descriptionAr`}
                        placeholder="Arabic Description"
                        as="textarea"
                        touched={touched}
                        errors={errors}
                        isArabic={true}
                      />
                    </div>
                  </Col>
                  <Col lg={6}>
                    <div className="form_field mt_13 mb-0">
                      <CustomLabel htmlFor="status" label={t("Status")} />
                      <div>
                        <InputSwitch
                          checked={values.status == 1}
                          onChange={(e) => {
                            const updatedStatus = values.status == 1 ? 0 : 1;
                            setFieldValue("status", updatedStatus);
                          }}
                        />
                      </div>
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="g_between mt-4 gap-3">
                      <button
                        type="button"
                        className="cancel_btn w-50"
                        onClick={handleClose}
                      >
                        {t("Cancel")}
                      </button>
                      <button
                        type="submit"
                        className="main_btn w-50"
                        disabled={isLoading}
                      >
                        {t("Update")}
                      </button>
                    </div>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default Notifications;
